import React, { ReactElement } from 'react';

import { Banner } from '@coachhubio/nova-banner';
import { borderRadius, getBorder } from '@coachhubio/nova-borders';
import { getBackgroundColor } from '@coachhubio/nova-colors';
import { Column, Row } from '@coachhubio/nova-flex';
import { WarningIcon } from '@coachhubio/nova-icon';
import { Img } from '@coachhubio/nova-img';
import { Skeleton } from '@coachhubio/nova-skeleton';
import { Text } from '@coachhubio/nova-text';
import ImpactPlusEmptyState from 'assets/impactPlusEmptyState.svg';
import { FormattedTranslation } from 'components/FormattedTranslation';
import { useLooker } from 'context/LookerContext';
import useGetLookerData from 'hooks/useGetLookerData';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ImpactPlusProgressTable from './components/ImpactPlusProgressTable';
import { textsColor } from './ImpactPlus.styles';
import { ImpactProgressOrgHasResultsData } from './ImpactPlus.types';
import ImpactPlusDashboardLayout from './ImpactPlusDashboardLayout';
import { InlinePlaceholder } from '../../components/Common';
import { LookerFieldsTag } from '../../utils/lookerFields';


const MultiProgramBanner = styled(Banner)`
  ${getBackgroundColor('error95')}

  ${getBorder({
    color: 'tertiaryMain90',
    radius: 'm',
    width: 's',
    positionRelative: false,
  })}
`;


function useSingleProgramSelected() {
  const { filters } = useLooker();
  return Boolean(
    filters?.programs &&
    filters.programs !== '' &&
    filters.programs.split(',').length === 1
  );
}

function ImpactPlusBanner() {
  const { t } = useTranslation('Dashboard');
  const singleProgramSelected = useSingleProgramSelected();

  return (
    <>
      {singleProgramSelected ? (
        <></>
      ) : (
        <MultiProgramBanner
          id="multier"
          description={
            <Row>
              <WarningIcon color="error35" />
              <Column marginStart="s">
                <Text color="error35" modifier="bold">
                  {t('impactPlus.multiprogram.title')}
                </Text>
                <Text color="neutral45" marginTop="none">
                  {t('impactPlus.multiprogram.description')}
                </Text>
              </Column>
            </Row>
          }
        />
      )}
    </>
  );
}

const MidSizeColumn = styled(Column)`
  width: 50%;
`;
function IPGraphBanner() {
  const { t } = useTranslation('Dashboard');
  return (
    <>
      <Row marginBottom="l">
        <MidSizeColumn marginTop="s" marginBottom="xs">
          <Text size="xl" modifier="bold" color={textsColor}>
            {t('impactPlus.title')}
          </Text>
          <Text size="s" color="neutral45" marginTop="s">
            {t('impactPlus.description')}
          </Text>
        </MidSizeColumn>
        <MidSizeColumn marginTop="m" marginEnd="xs">
          {/* <ImpactReportDownload /> */}
        </MidSizeColumn>
      </Row>
    </>
  );
}

const WhiteColumn = styled(Column)`
  ${getBackgroundColor('neutral')}
  ${getBorder({
    color: 'neutral85',
    radius: 'm',
    width: 's',
  })}
  ${borderRadius('m')}
  height: 60%;
`;
const LeftColumn = styled(Column)`
  width: 50%;
`;

function IPNoCHFeedback() {
  const { t } = useTranslation('Dashboard');
  function NoCHFeedbackBanner() {
    return (
      <Row marginStart="xl">
        <Column marginTop="s" marginBottom="xs" marginEnd="5xl">
          <Text modifier="bold" size="m" color="text30">
            {t('impactPlus.no-data.title')}
          </Text>
          <Text size="xs" color="neutral45" marginTop="m">
            <FormattedTranslation t={t} i18nKey={`impactPlus.no-data.description`} values={{}} />
          </Text>
        </Column>
      </Row>
    );
  }

  return (
    <WhiteColumn marginX="l" justifyContent="center">
      <Row justifyContent="space-around" margin="2xl">
        <LeftColumn alignItems="center" justifyContent="center">
          <NoCHFeedbackBanner />
        </LeftColumn>
        <Column marginEnd="xl">
          <Img src={ImpactPlusEmptyState} alt={t('impactPlus.no-data.imageAlt')} width={276} height={238} />
        </Column>
      </Row>
    </WhiteColumn>
  );
}
const ImpactPlusDashboard = (): ReactElement => {
  const { data: orgHasResultsArray, loading } = useGetLookerData<ImpactProgressOrgHasResultsData[]>(
    LookerFieldsTag.ImpactProgressOrgHasResults
  );

  const orgHasAnyResults: boolean = (orgHasResultsArray && orgHasResultsArray.length > 0) || false;
  const orgHasResults: boolean =
    orgHasAnyResults &&
    (orgHasResultsArray || false) &&
    orgHasResultsArray.filter((item) => item['coachhub_feedback_assignments.has_results'] === 'true').length > 0;
  const orgHasSelfResults: boolean =
    orgHasAnyResults &&
    (orgHasResultsArray || false) &&
    orgHasResultsArray.filter((item) => item['coachhub_feedback_assignments.has_self_assessment_results'] === 'true')
      .length > 0;

  const hasCHFeedback: boolean = orgHasAnyResults && (orgHasResults || orgHasSelfResults);

  return (
    <ImpactPlusDashboardLayout>
      {loading ? (
        <InlinePlaceholder $height="400px" $width="100%">
          <Skeleton height="400px" width="100%" />
        </InlinePlaceholder>
      ) : !hasCHFeedback ? (
        <IPNoCHFeedback />
      ) : (
        <>
          <ImpactPlusBanner />
          <IPGraphBanner />
          <ImpactPlusProgressTable orgHasResults={orgHasResults} orgHasSelfResults={orgHasSelfResults} />
        </>
      )}
    </ImpactPlusDashboardLayout>
  );
};

export default ImpactPlusDashboard;
